import {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import RFB, { RFBEvent } from '@novnc/novnc/core/rfb';
import { omit } from 'ramda';
import { useXrayRemoteControlConnection } from '@bymason/controller-api-hooks';
import { useIsMounted } from '@bymason/util-hooks';
import { XrayRemoteControlMethods, XrayRemoteControlProps, XrayRemoteControlRfbError } from './types';

const SECONDS_BEFORE_TIMEOUT_TO_ISSUE_WARNING = 60 * 2; // TWO MINUES
const GENERIC_ERROR = 'The X-Ray Remote-Control viewer cannot be launched at this time. Please try again.';
const VIEWER_ROOT_ID = 'xrayViewerRoot';

export * from './types';
export { useGetLogBuffers, useXrayLogStreamConnection } from '@bymason/controller-api-hooks';

const XrayRemoteControl: ForwardRefRenderFunction<XrayRemoteControlMethods, XrayRemoteControlProps> = (
  {
    clientToken,
    deviceId,
    pendingBackgroundColor,
    viewportBorderRadius,
    onConnectionInfo,
    onError,
    onStart,
    onStop,
    onTimeout,
    onTimeoutWarning,
  }: XrayRemoteControlProps,
  ref
) => {
  const isMounted = useIsMounted();
  const [rfbError, setRfbError] = useState<XrayRemoteControlRfbError | null>(null);
  const [
    startConnection,
    stopConnection,
    dataChannel,
    connectionInfo,
    connectionTimedOut,
    connectionError,
  ] = useXrayRemoteControlConnection(deviceId, clientToken);
  const [rfb, setRfb] = useState<RFB | null>(null);
  const [sessionWasStopped, setSessionWasStopped] = useState(false);

  const stopAndReset = useCallback(() => {
    rfb?.disconnect();
    stopConnection();

    if (isMounted()) {
      setRfb(null);
      setRfbError(null);
      setSessionWasStopped(true);
    }

    if (onStop) {
      onStop();
    }
  }, [isMounted, onStop, rfb, stopConnection]);

  useImperativeHandle(ref, () => ({
    start: () => {
      setSessionWasStopped(false);
      startConnection();
    },
    stop: () => {
      stopAndReset();
    },
  }));

  //
  // handle errors
  //
  useEffect(() => {
    if (rfbError) {
      console.error(rfbError.technicalError);

      if (onError) {
        onError(rfbError.friendlyError || GENERIC_ERROR);
      }

      stopAndReset();
    }
  }, [onError, rfbError, stopAndReset]);

  //
  // handle init of new RFB upon creation of data channel
  //
  useEffect(() => {
    if (dataChannel && !rfb && !sessionWasStopped) {
      const xrayViewerRoot = document.getElementById(VIEWER_ROOT_ID);

      if (!xrayViewerRoot) {
        setRfbError({
          technicalError: `The X-Ray Remote-Control Viewer's root html element "${VIEWER_ROOT_ID}" was not found on the DOM.`,
        });
        return;
      }

      try {
        const newRfb = new RFB(xrayViewerRoot, dataChannel);

        newRfb.scaleViewport = true;
        newRfb.resizeSession = true;
        newRfb.showDotCursor = true;

        /**
         *  Controls the desired JPEG quality.
         *  - 0 implies low quality
         *  - 9 implies high quality
         *  - If not specified, defaults to 6
         */
        newRfb.qualityLevel = 9;

        /**
         *  Controls the desired compression level.
         *  - Value 0 means no compression.
         *  - Level 1 uses a minimum of CPU resources and achieves weak compression ratios
         *  - Level 9 offers best compression but is slow in terms of CPU consumption on server
         *  - Use high levels with very slow network connections
         *  - If not specified, defaults to 2
         *
         * TODO: Implement heuristics so that as connection speed declines, a higher
         * compression value is used
         **/
        newRfb.compressionLevel = 2;

        newRfb.addEventListener('connect', () => {
          if (onStart) {
            onStart();
          }
        });

        newRfb.addEventListener('credentialsrequired', (evt: RFBEvent) => {
          setRfbError({
            technicalError: `The X-Ray Remote-Control session received a "credentials required" error${
              evt.detail.reason && `: ${evt.detail.reason}`
            }.`,
          });

          stopAndReset();
        });

        newRfb.addEventListener('disconnect', (evt: RFBEvent) => {
          if (!evt.detail.clean) {
            setRfbError({
              friendlyError:
                'The X-Ray Remote-Control session disconnected unexpectedly. Please check your network connection.',
              technicalError: 'The X-Ray Remote-Control RFB session had a dirty disconnect.',
            });
          }

          stopAndReset();
        });

        newRfb.addEventListener('securityfailure', (evt: RFBEvent) => {
          setRfbError({
            technicalError: `The X-Ray Remote-Control session received a "security failure" error${
              evt.detail.reason && `: ${evt.detail.reason}`
            }.`,
          });

          stopAndReset();
        });

        setRfb(newRfb);
      } catch (error) {
        setRfbError({
          technicalError: `Error while initializing the RFB session: ${
            error instanceof Error ? error.message : error
          }`,
        });

        stopAndReset();
      }
    }
  }, [dataChannel, onStart, rfb, sessionWasStopped, stopAndReset]);

  //
  // handle issuing a timeout warning
  //
  useEffect(() => {
    if (
      onTimeoutWarning &&
      connectionInfo &&
      connectionInfo.secondsBeforeTimeout &&
      connectionInfo.secondsBeforeTimeout <= SECONDS_BEFORE_TIMEOUT_TO_ISSUE_WARNING
    ) {
      onTimeoutWarning(connectionInfo.secondsBeforeTimeout, () => {
        connectionInfo.resetPendingTimeout && connectionInfo.resetPendingTimeout();
      });
    }
  }, [connectionInfo, onTimeoutWarning]);

  //
  // handle a connection timeout
  //
  useEffect(() => {
    if (connectionTimedOut) {
      stopAndReset();

      if (onTimeout) {
        onTimeout();
      }
    }
  }, [connectionTimedOut, onTimeout, stopAndReset]);

  //
  // handle connection report/info
  //
  useEffect(() => {
    if (onConnectionInfo && connectionInfo) {
      // remove the reset callback before sending back info
      const normConnectionInfo = omit(['resetPendingTimeout'], connectionInfo);

      onConnectionInfo(normConnectionInfo);
    }
  }, [connectionInfo, onConnectionInfo]);

  //
  // handle connection error
  //
  useEffect(() => {
    if (connectionError) {
      setRfbError({ technicalError: connectionError });
    }
  }, [connectionError]);

  //
  // handle browser-close/navigation-away
  // without user first stopping session
  //
  useEffect(() => {
    return () => {
      if (!isMounted()) {
        stopAndReset();
      }
    };
  }, [isMounted, stopAndReset]);

  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <div
        id={VIEWER_ROOT_ID}
        style={{
          backgroundColor: pendingBackgroundColor || '#A30DDB',
          borderRadius: viewportBorderRadius || 0,
          height: '100%',
          width: '100%',
        }}
      />
    </div>
  );
};
export default forwardRef(XrayRemoteControl);
